/**
 * A card header's actions for one entity record: the "..." menu of the
 * tenant's configured actions, then the platform edit pen.
 *
 * Editing is the action a card is asked for most, so it is never buried in the
 * menu: the record's edit action is lifted out and shown as the shared
 * EditIconButton, last in the header - the same pen every editable card shows.
 * The menu keeps the remaining actions and hides its trigger when edit was the
 * only one, so no empty menu renders.
 *
 * The pen edits the details the card shows: only the card's general edit, the
 * action the caller names (`edit_customer`, `edit_host`). A narrower edit
 * (change address, update owner) is a named action and stays in the menu, and
 * a record without the general edit shows no pen. The dialog, reason gate and
 * permission filter all stay in EntityActionMenu - the pen only triggers it -
 * and the actions come back already filtered by permission, so presence is the
 * gate.
 */

import { useState, type ReactNode } from 'react'

import { EditIconButton } from '@/admin/components/shared/edit-icon-button'
import { EntityActionMenu } from '@/admin/components/shared/entity-action-menu'
import { useEntityTypeByKey } from '@/admin/hooks/use-entities'
import {
  useEvaluatedEntityActions,
  type EntityAction,
} from '@/admin/hooks/use-entity-actions'

/** The platform's default host edit (every editable host field). */
export const EDIT_HOST_ACTION_KEY = 'edit_host'

export function EntityCardActions({
  entityTypeKey,
  resourceUuid,
  holdingUuid,
  editActionKey,
  editButtonTestId,
  onActionComplete,
  onNavigate,
  triggerClassName,
  triggerLabel,
  triggerTestId,
}: {
  entityTypeKey: string
  resourceUuid: string
  /** The registration the card is on, for actions scoped to it. */
  holdingUuid?: string
  /** The card's general edit; never a narrower one picked for it. */
  editActionKey: string
  /** Defaults to `card-action-<edit action key>`. */
  editButtonTestId?: string
  onActionComplete?: () => void
  onNavigate?: (action: EntityAction) => void
  triggerClassName?: string
  triggerLabel?: string
  triggerTestId?: string
}): ReactNode {
  const [triggeredActionKey, setTriggeredActionKey] = useState<string | null>(
    null,
  )

  const { data: entity } = useEntityTypeByKey(entityTypeKey)
  const { data: evaluated } = useEvaluatedEntityActions(
    entity?.uuid,
    resourceUuid,
    holdingUuid,
  )

  const actions = evaluated?.actions ?? []
  const editAction = actions.find(
    (action) =>
      action.key === editActionKey && action.action_kind === 'edit_fields',
  )
  const hasOtherActions = actions.some(
    (action) => action.key !== editAction?.key,
  )

  if (!editAction && !hasOtherActions) return null

  return (
    <>
      <EntityActionMenu
        entityTypeKey={entityTypeKey}
        resourceUuid={resourceUuid}
        holdingUuid={holdingUuid}
        onActionComplete={onActionComplete}
        onNavigate={onNavigate}
        triggerLabel={triggerLabel}
        triggerTestId={triggerTestId}
        triggerClassName={triggerClassName}
        contentClassName="min-w-[210px]"
        excludeActionKeys={editAction ? [editAction.key] : undefined}
        hideTrigger={!hasOtherActions}
        externalTriggerActionKey={triggeredActionKey}
        onExternalTriggerReset={() => setTriggeredActionKey(null)}
      />
      {editAction ? (
        <EditIconButton
          testId={editButtonTestId ?? `card-action-${editAction.key}`}
          label={editAction.label || undefined}
          onClick={() => setTriggeredActionKey(editAction.key)}
        />
      ) : null}
    </>
  )
}
